import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  ArrowForward as ArrowIcon,
  LocalShipping,
  Business,
  Inventory,
  ContactMail,
  Home as HomeIcon,
  WhatsApp as WhatsAppIcon,
  ChevronRight,
} from "@mui/icons-material";

// Import your logo image
import logoImage from "../assets/images/logo.png";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Brand colors
  const brandColors = {
    green: '#a6ce39',
    black: '#050606',
    orange: '#ec7723',
    white: '#ffffff',
    lightGray: '#f8fafc',
    darkGray: '#1a202c'
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Keeping EXACT same link names as specified
  const menuItems = [
    {
      text: "Home",
      path: "/",
      icon: <HomeIcon />
    },
    {
      text: "About",
      path: "/about",
      icon: <Business />
    },
    {
      text: "Our Trucks", // Keeping this name as specified
      path: "/trucks",
      icon: <LocalShipping />
    },
    {
      text: "Booking", // Keeping this name as specified
      path: "/booking",
      icon: <Inventory />
    },
    {
      text: "Contact",
      path: "/contact",
      icon: <ContactMail />
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      sx={{
        width: 360,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: brandColors.lightGray,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Modern Glass Effect Header */}
      <Box sx={{ 
        p: 4, 
        background: `linear-gradient(135deg, ${brandColors.black} 0%, ${brandColors.darkGray} 100%)`,
        color: brandColors.white,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%231a202c" stroke-width="0.5"/></pattern></defs><rect width="100%25" height="100%25" fill="url(%23grid)"/></svg>')`,
          opacity: 0.1,
        }
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Box
            component={Link}
            to="/"
            onClick={() => setMobileOpen(false)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              textDecoration: "none",
            }}
          >
            {/* Larger Modern Logo in Drawer */}
            <Box
              sx={{
                width: 80, // Increased width
                height: 80, // Increased height
                borderRadius: '18px',
                background: brandColors.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 2,
                boxShadow: `0 10px 30px ${alpha(brandColors.orange, 0.15)}`,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, transparent 30%, ${alpha(brandColors.white, 0.1)} 50%, transparent 70%)`,
                  animation: 'shimmer 3s infinite linear',
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  }
                },
                '&:hover': {
                  transform: 'scale(1.05) rotate(2deg)',
                  boxShadow: `0 15px 40px ${alpha(brandColors.orange, 0.25)}`,
                }
              }}
            >
              <Box
                component="img"
                src={logoImage}
                alt="TRANSCO LOGISTICS Logo"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: brandColors.white,
                  lineHeight: 1.1,
                  fontSize: '1.5rem',
                  mb: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                TRANSCO
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: brandColors.green,
                  letterSpacing: '2px',
                  textTransform: "uppercase",
                  fontSize: "0.8rem",
                  display: "block",
                }}
              >
                LOGISTICS
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: brandColors.white,
              background: alpha(brandColors.white, 0.1),
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              width: 48,
              height: 48,
              border: `1px solid ${alpha(brandColors.white, 0.2)}`,
              "&:hover": {
                background: alpha(brandColors.orange, 0.3),
                color: brandColors.orange,
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Chip
          label="24/7 Logistics Support"
          size="small"
          sx={{
            background: alpha(brandColors.orange, 0.2),
            color: brandColors.orange,
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 28,
            border: `1px solid ${alpha(brandColors.orange, 0.3)}`,
            '& .MuiChip-label': {
              px: 1.5,
            }
          }}
        />
      </Box>

      {/* Modern Navigation List */}
      <Box sx={{ 
        flex: 1, 
        px: 3,
        py: 4,
        background: brandColors.lightGray,
      }}>
        <Typography
          variant="caption"
          sx={{
            mb: 3,
            display: 'block',
            fontWeight: 700,
            color: alpha(brandColors.black, 0.6),
            textTransform: 'uppercase',
            letterSpacing: '2px',
            fontSize: '0.75rem',
          }}
        >
          Navigation
        </Typography>
        
        <List sx={{ p: 0 }}>
          {menuItems.map((item, index) => (
            <ListItem
              key={item.text}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                mb: 2,
                borderRadius: '14px',
                background: location.pathname === item.path
                  ? brandColors.white
                  : alpha(brandColors.white, 0.7),
                border: location.pathname === item.path
                  ? `2px solid ${brandColors.orange}`
                  : `1px solid ${alpha(brandColors.black, 0.08)}`,
                boxShadow: location.pathname === item.path
                  ? `0 8px 24px ${alpha(brandColors.orange, 0.1)}`
                  : '0 2px 8px rgba(0,0,0,0.03)',
                "&:hover": {
                  background: brandColors.white,
                  borderColor: brandColors.orange,
                  boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.12)}`,
                  transform: 'translateX(8px)',
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                textDecoration: "none",
                pl: 3,
                pr: 2,
                py: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path
                    ? brandColors.orange
                    : alpha(brandColors.black, 0.6),
                  minWidth: 44,
                  mr: 2.5,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 800 : 700,
                  color: location.pathname === item.path
                    ? brandColors.orange
                    : brandColors.black,
                  fontSize: "1rem",
                  letterSpacing: '-0.2px',
                }}
              />
              <ChevronRight
                sx={{
                  fontSize: 20,
                  color: location.pathname === item.path
                    ? brandColors.orange
                    : alpha(brandColors.black, 0.4),
                  transition: 'all 0.3s ease',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Modern Contact Section */}
      <Box sx={{ 
        p: 4, 
        background: brandColors.white,
        borderTop: '1px solid',
        borderColor: alpha(brandColors.black, 0.06),
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -20,
          left: 0,
          right: 0,
          height: 20,
          background: `linear-gradient(to bottom, transparent, ${brandColors.white})`,
        }
      }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: brandColors.black,
            mb: 3,
            textTransform: "uppercase",
            letterSpacing: '2px',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: brandColors.green,
              boxShadow: `0 0 8px ${brandColors.green}`,
            }}
          />
          Contact Us
        </Typography>
        
        <Button
          fullWidth
          variant="contained"
          startIcon={<PhoneIcon />}
          component="a"
          href="tel:+26876021597"
          sx={{
            mb: 2,
            py: 1.8,
            borderRadius: '14px',
            fontWeight: 700,
            fontSize: "0.95rem",
            background: `linear-gradient(135deg, ${brandColors.black} 0%, ${brandColors.darkGray} 100%)`,
            boxShadow: `0 8px 24px ${alpha(brandColors.black, 0.15)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, transparent 30%, ${alpha(brandColors.white, 0.1)} 50%, transparent 70%)`,
              animation: 'shimmer 3s infinite linear',
              '@keyframes shimmer': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(100%)' },
              }
            },
            "&:hover": {
              background: brandColors.black,
              transform: "translateY(-3px)",
              boxShadow: `0 12px 32px ${alpha(brandColors.black, 0.25)}`,
            },
            transition: "all 0.3s ease",
          }}
        >
          +268 7602 1597
        </Button>
        
        <Button
          fullWidth
          variant="contained"
          endIcon={<ArrowIcon />}
          component={Link}
          to="/booking"
          onClick={() => setMobileOpen(false)}
          sx={{
            py: 1.8,
            borderRadius: '14px',
            fontWeight: 700,
            fontSize: "0.95rem",
            background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
            boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.2)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, transparent 30%, ${alpha(brandColors.white, 0.15)} 50%, transparent 70%)`,
              animation: 'shimmer 3s infinite linear',
              '@keyframes shimmer': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(100%)' },
              }
            },
            "&:hover": {
              background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
              transform: "translateY(-3px)",
              boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.3)}`,
            },
            transition: "all 0.3s ease",
          }}
        >
          Book Now
        </Button>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 3,
            textAlign: 'center',
            color: alpha(brandColors.black, 0.6),
            fontWeight: 500,
          }}
        >
          transcologistics26@gmail.com
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled
            ? `linear-gradient(180deg, ${alpha(brandColors.white, 0.98)} 0%, ${alpha(brandColors.white, 0.96)} 100%)`
            : brandColors.white,
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? 'none' : '1px solid',
          borderColor: alpha(brandColors.black, 0.06),
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: scrolled
            ? `0 10px 40px ${alpha(brandColors.black, 0.08)}`
            : '0 2px 12px rgba(0,0,0,0.02)',
          zIndex: 1300,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: scrolled ? 1.5 : 2.5,
              transition: "all 0.3s ease",
              minHeight: { xs: '80px', md: '90px' },
            }}
          >
            {/* Larger Modern Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                flexGrow: { xs: 1, lg: 0 },
                mr: { lg: 'auto' },
                transition: 'all 0.3s ease',
                // Significantly increased logo size
                width: { xs: 75, md: scrolled ? 90 : 100 }, // Increased width
                height: { xs: 75, md: scrolled ? 90 : 100 }, // Increased height
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: scrolled ? '20px' : '22px',
                  background: brandColors.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: scrolled ? 2 : 2.5,
                  boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.15)}`,
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `2px solid ${alpha(brandColors.orange, 0.15)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, transparent 30%, ${alpha(brandColors.white, 0.1)} 50%, transparent 70%)`,
                    animation: 'shimmer 3s infinite linear',
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' },
                    }
                  },
                  "&:hover": {
                    transform: "scale(1.05) rotate(1deg)",
                    boxShadow: `0 16px 48px ${alpha(brandColors.orange, 0.25)}`,
                    borderColor: brandColors.orange,
                  },
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="TRANSCO LOGISTICS Logo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            </Box>

            {/* Modern Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ 
                display: "flex", 
                flex: 1, 
                alignItems: "center", 
                justifyContent: "center",
                mx: 6,
              }}>
                <Box sx={{ 
                  display: "flex", 
                  gap: 1,
                  background: alpha(brandColors.black, 0.02),
                  padding: 1,
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: alpha(brandColors.black, 0.05),
                  backdropFilter: 'blur(10px)',
                  boxShadow: `0 8px 24px ${alpha(brandColors.black, 0.03)}`,
                }}>
                  {menuItems.map((item) => (
                    <Button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path
                          ? brandColors.white
                          : alpha(brandColors.black, 0.7),
                        fontWeight: location.pathname === item.path ? 800 : 600,
                        position: "relative",
                        fontSize: scrolled ? "0.92rem" : "0.95rem",
                        px: scrolled ? 3 : 3.5,
                        py: scrolled ? 1.2 : 1.5,
                        borderRadius: '12px',
                        textTransform: "none",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        minWidth: "auto",
                        background: location.pathname === item.path
                          ? `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`
                          : 'transparent',
                        boxShadow: location.pathname === item.path
                          ? `0 6px 20px ${alpha(brandColors.orange, 0.2)}`
                          : 'none',
                        overflow: 'hidden',
                        "&:hover": {
                          color: location.pathname === item.path ? brandColors.white : brandColors.orange,
                          background: location.pathname === item.path
                            ? `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`
                            : alpha(brandColors.orange, 0.05),
                          transform: "translateY(-2px)",
                          boxShadow: location.pathname === item.path
                            ? `0 8px 24px ${alpha(brandColors.orange, 0.3)}`
                            : `0 4px 12px ${alpha(brandColors.orange, 0.1)}`,
                        },
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* Modern Desktop CTA Buttons */}
            {!isMobile && (
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 2, 
                ml: "auto",
              }}>
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  component="a"
                  href="tel:+26876021597"
                  sx={{
                    borderWidth: 2,
                    borderColor: alpha(brandColors.green, 0.5),
                    color: brandColors.green,
                    borderRadius: '14px',
                    px: scrolled ? 3 : 3.5,
                    py: scrolled ? 1 : 1.2,
                    fontWeight: 700,
                    fontSize: scrolled ? "0.9rem" : "0.95rem",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    background: alpha(brandColors.white, 0.8),
                    backdropFilter: 'blur(10px)',
                    "&:hover": {
                      borderColor: brandColors.green,
                      color: brandColors.green,
                      background: alpha(brandColors.green, 0.05),
                      transform: "translateY(-3px)",
                      boxShadow: `0 8px 24px ${alpha(brandColors.green, 0.15)}`,
                    },
                  }}
                >
                  Call Us
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowIcon />}
                  component={Link}
                  to="/booking"
                  sx={{
                    background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                    borderRadius: '14px',
                    px: scrolled ? 4 : 5,
                    py: scrolled ? 1.2 : 1.5,
                    fontWeight: 700,
                    fontSize: scrolled ? "0.95rem" : "1rem",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(45deg, transparent 30%, ${alpha(brandColors.white, 0.15)} 50%, transparent 70%)`,
                      animation: 'shimmer 3s infinite linear',
                      '@keyframes shimmer': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(100%)' },
                      }
                    },
                    "&:hover": {
                      background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                      transform: "translateY(-3px)",
                      boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.3)}`,
                    },
                  }}
                >
                  Book Now
                </Button>
              </Box>
            )}

            {/* Modern Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  ml: "auto",
                  color: brandColors.orange,
                  background: alpha(brandColors.orange, 0.1),
                  borderRadius: '14px',
                  width: 52,
                  height: 52,
                  border: '2px solid',
                  borderColor: alpha(brandColors.orange, 0.2),
                  "&:hover": {
                    background: alpha(brandColors.orange, 0.2),
                    borderColor: brandColors.orange,
                    transform: 'scale(1.05) rotate(90deg)',
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            boxShadow: `0 0 60px ${alpha(brandColors.black, 0.2)}`,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer for fixed AppBar */}
      <Toolbar sx={{ minHeight: { xs: '80px', md: '90px' } }} />
    </>
  );
};

export default Navbar;